import { tva } from '@gluestack-ui/utils/nativewind-utils'

import { colors } from '../../tokens'

export type ButtonVariant = 'primary' | 'secondary' | 'text'
export type ButtonTone = 'default' | 'destructive'
export type ButtonSize = 'small' | 'big'

// Internal style resolution for the prop-driven Button. Two orthogonal axes:
// `variant` = presentation (solid / outline / text), `tone` = semantic colour
// (default action-blue / destructive red). These are NOT part of the public API
// — callers pass `<Button variant tone />`; the resolvers below are consumed only
// by Button.tsx (container) and its ButtonText/ButtonIcon children (via context).
//
// Figma "Primary" blue is `Primary/default` = #3459c6, mapped to the AOS
// `action` token group (NOT `aos-primary`), so buttons render identically across
// tenants — same decision as the pickers redesign (see ARCHITECTURE.md).
// Secondary keeps a light label (`content/default`): the colour lives in the
// border/ink only, never the label. The filled destructive button uses the
// destructive/* fills with a content/knockout label, as Figma pairs them.

// The DS contract (Figma component set "Button") carries the height on the
// `size` axis: `big` = 3rem, `small` = 2.5rem. Padding never adds up to it.
export const buttonHeight: Record<ButtonSize, string> = {
    big: 'h-12',
    small: 'h-10',
}

const buttonContainer = tva({
    variants: {
        tone: { default: '', destructive: '' },
        variant: { primary: '', secondary: '', text: '' },
    },
    compoundVariants: [
        {
            tone: 'default',
            variant: 'primary',
            class: 'bg-action-default hover:bg-action-hover active:bg-action-pressed',
        },
        {
            tone: 'default',
            variant: 'secondary',
            class: 'border-2 border-action-default bg-transparent hover:bg-action-hover active:bg-action-pressed',
        },
        {
            tone: 'default',
            variant: 'text',
            class: 'border-0 bg-transparent hover:bg-aos-bg-hover-layer active:bg-aos-bg-surface',
        },
        {
            tone: 'destructive',
            variant: 'primary',
            class: 'bg-destructive-default hover:bg-destructive-hover active:bg-destructive-pressed',
        },
        {
            tone: 'destructive',
            variant: 'secondary',
            class: 'border-2 border-status-critical bg-transparent hover:bg-status-critical-hover active:bg-status-critical-pressed',
        },
        {
            tone: 'destructive',
            variant: 'text',
            class: 'border-0 bg-transparent hover:bg-aos-bg-hover-layer active:bg-aos-bg-surface',
        },
    ],
})

// Label AND icon ink: a className (the glyph paints `currentColor`), never a raw
// hex — a hex can't follow the hover/pressed fill, which left the icon light on
// the brand-filled secondary button (AOS-1187, yellow on AOIMS).
const buttonLabel = tva({
    variants: {
        tone: { default: '', destructive: '' },
        variant: { primary: '', secondary: '', text: '' },
    },
    compoundVariants: [
        { tone: 'default', variant: 'primary', class: 'text-aos-text-on-primary' },
        {
            tone: 'default',
            variant: 'secondary',
            class: 'text-aos-text-default group-hover/button:text-aos-text-on-primary group-active/button:text-aos-text-on-primary',
        },
        { tone: 'default', variant: 'text', class: 'text-aos-text-default' },
        { tone: 'destructive', variant: 'primary', class: 'text-aos-text-knockout' },
        { tone: 'destructive', variant: 'secondary', class: 'text-aos-text-default' },
        { tone: 'destructive', variant: 'text', class: 'text-status-critical' },
    ],
})

export const resolveButtonContainer = (variant: ButtonVariant, tone: ButtonTone): string =>
    buttonContainer({ variant, tone })

export const resolveButtonLabel = (variant: ButtonVariant, tone: ButtonTone): string =>
    buttonLabel({ variant, tone })

// `ButtonSpinner` renders an ActivityIndicator, which takes a colour VALUE and
// ignores a className — so the label ink above is mirrored here as tokens.
export const resolveButtonSpinnerColor = (variant: ButtonVariant, tone: ButtonTone): string => {
    if (variant === 'primary') {
        return tone === 'destructive'
            ? colors['aos-text'].knockout
            : colors['aos-text']['on-primary']
    }
    if (variant === 'text' && tone === 'destructive') {
        return colors.status.critical
    }
    return colors['aos-text'].default
}
